import { useTranslation } from "@msuite/ui/localization";
import { getPartnershipRole } from "@msuite/utils/partnerships";
import type { Nullable, Tables } from "@msuite/utils/types";
import { useAuth } from "@/hooks/use-auth";

/**
 * Names the direction of a partnership row from the viewer's side: "We book {name}" on a row the
 * viewer hosts, "{name} books us" on a row the counterpart hosts.
 *
 * @returns The viewer's role resolver and the direction label for a row and its counterpart's name.
 */
export function usePartnershipDirection() {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { organization } = useAuth();

	/** Functions */
	function getRole(partnership: Nullable<Tables<"organizationPartnerships">>) {
		return getPartnershipRole(organization.id, partnership);
	}

	function getDirectionLabel(partnership: Tables<"organizationPartnerships">, name: string) {
		const role = getRole(partnership);
		if (role === "host") return t("partners.direction_we_book", { name });
		if (role === "partner") return t("partners.direction_they_book", { name });
		return null;
	}

	/** Return */
	return { getRole, getDirectionLabel };
}
